import { useCallback, useEffect, useRef, useState } from "react";
import { CallModal } from "@/pages/Chats/CallModal";
import { useSoftphone, type Softphone } from "@/pages/Chats/softphone";
import {
  initialOutgoingCall,
  stepOutgoing,
  type ClosedCall,
  type OutgoingAction,
  type OutgoingCallState,
} from "./outgoingCall";
import { registerOutgoingCallHost, setAfterCallLayer } from "./useOutgoingCall";

function notify(closed: ClosedCall | null) {
  closed?.onClosed({ talked: closed.talked });
}

// Исходящий звонок на весь шелл: окно звонка (CallModal) и его софтфон. Живёт
// в AppShell и MobileApp рядом с InboundCallHost — внутри роутера (окну нужна
// ссылка на карточку) и только для вошедших. В программе для Windows (tray/)
// его нет: оттуда не звонят.
//
// Софтфон принадлежит хосту, а не странице: раньше им владели лента чата и
// карточка кандидата, и звонок обрывался, стоило уйти со страницы или закрыть
// карточку. Теперь окно сворачивается в плашку, а под ним можно открыть
// карточку, сменить чат или раздел — как с входящим.
//
// Страницы звонок только заказывают: useOutgoingCall().open(). Что при этом
// происходит с окном — в outgoingCall.ts, чистой функцией.
export function OutgoingCallHost() {
  const phone = useSoftphone();
  const [state, setState] = useState<OutgoingCallState>(initialOutgoingCall);
  // Состояние — ещё и синхронно, в обход перерисовки: open() из клика на
  // другой странице должен видеть звонок, начатый мгновением раньше.
  const stateRef = useRef(state);
  // Софтфон — через ref: колбэки ниже живут дольше рендера.
  const phoneRef = useRef(phone);
  phoneRef.current = phone;

  const apply = useCallback((action: OutgoingAction): ClosedCall | null => {
    const step = stepOutgoing(stateRef.current, action);
    stateRef.current = step.state;
    setState(step.state);
    return step.closed;
  }, []);

  useEffect(
    () =>
      registerOutgoingCallHost({
        open: (req, opener) => {
          const before = stateRef.current.session;
          const closed = apply({ type: "open", req, opener });
          // Новое окно — без хвоста прошлого звонка: иначе оно открылось бы
          // сразу с «завершён» и закрылось само через пару секунд, не дав
          // набрать. Идущий звонок сюда не попадает: окно осталось прежним.
          if (stateRef.current.session !== before) phoneRef.current.reset();
          notify(closed);
        },
        release: (opener) => {
          apply({ type: "opener_gone", opener });
        },
      }),
    [apply],
  );

  // Линия софтфона → состояние окна: был ли разговор, не пора ли убрать
  // плашку (трубку положили до ответа).
  const line = phone.status.state;
  useEffect(() => {
    notify(apply({ type: "line", line }));
  }, [line, apply]);

  const dial = useCallback(
    (opts: Parameters<Softphone["dial"]>[0]) => {
      // «Соединение…» — сразу, не дожидаясь перерисовки: софтфон уже занят, и
      // open() в этот миг не должен принять окно за свободное.
      apply({ type: "line", line: "connecting" });
      return phoneRef.current.dial(opts);
    },
    [apply],
  );

  const minimize = useCallback(
    (minimized: boolean) => {
      apply({ type: minimized ? "minimize" : "expand" });
    },
    [apply],
  );

  // Закрыть окно: крестик до набора, итог показан («завершён» — через пару
  // секунд само, см. CallModal), «Закрыть» на плашке с ошибкой.
  const close = useCallback(() => {
    if (!stateRef.current.session) return;
    const closed = apply({ type: "close" });
    void phoneRef.current.hangup();
    // Сброс обязателен: без него статус остаётся «завершён», и следующее окно
    // сразу закрылось бы само.
    phoneRef.current.reset();
    notify(closed);
  }, [apply]);

  const { session, minimized, notice } = state;
  return (
    <>
      {/* Слой для окна, которое страница открывает следом за звонком (вопрос
          про обратную связь после разговора с ЛПР, см. useAfterCallLayer): над
          карточкой кандидата, как и окно звонка. В DOM всегда — окно приходит,
          когда звонка уже нет. В разметке — до окна звонка: набирают следующий
          номер, не дожидаясь итога прошлого, — новое окно встаёт поверх
          вопроса, и Esc с «Назад» сперва убирают его (оно открыто последним). */}
      <div className="ocall-layer" ref={setAfterCallLayer} />
      {session && (
        <CallModal
          key={session.id}
          personName={session.personName}
          phones={session.phones}
          candidateId={session.candidateId}
          status={phone.status}
          localStream={phone.streams.local}
          remoteStream={phone.streams.remote}
          onDial={dial}
          onHangup={phone.hangup}
          audioBlocked={phone.audioBlocked}
          onResumeAudio={phone.resumeAudio}
          micMuted={phone.micMuted}
          onToggleMic={phone.toggleMic}
          holdSince={phone.holdSince}
          onToggleHold={() => void phone.toggleHold()}
          minimized={minimized}
          onMinimize={minimize}
          notice={notice}
          onClose={close}
        />
      )}
    </>
  );
}
